import React, { useEffect, useState } from "react";
import { Tag } from "primereact/tag";
import AvatarC from "../../common/avatar";
import { PropertyListCardProps } from "./property-list-card.interface";
import styles from "../property-list-card/property-list-card.module.scss";
import {
  countDaysFromDate,
  formatCurrency,
  numberWithCommas
} from "../../../utils/utils";
import { useNavigate } from "react-router-dom";
import { scrollToTop } from "../../../utils/const";
import Like from "../like";
import moment from "moment";
import { NavigateToExchange } from "../../../utils/inv-exch-amplitude";
import { NavigatePropertyDetail } from "../../investor/amplitude";
import PriceDropTag from "../pricedrop-tag";
import { Tooltip } from "primereact/tooltip";

const PropertyListCard = ({
  id,
  lat,
  lng,
  imageUrl,
  score,
  shape,
  listedTitle,
  agentFirstName,
  agentLastName,
  salesInstruction,
  defaultProfile,
  street,
  location,
  amount,
  currencyCode,
  locale,
  propertyId,
  park,
  property,
  bedRooms,
  city,
  state,
  postalCode,
  favCount,
  onHover,
  onLeave,
  isPlaceholderData,
  className,
  totalBath,
  sqft,
  status,
  statusValue,
  type,
  activatedAt,
  sfid,
  loginType,
  shareKey,
  offerDueDateAndTime,
  opportunitySfid,
  pubPropertySfid,
  objPropertyDetails
}: PropertyListCardProps) => {
  const amount1 = amount || 0;
  const navigate = useNavigate();

  const checkActivationTime = () => {
    if (activatedAt === null) return false;
    return (
      moment.utc().diff(moment.utc(activatedAt), "minutes") >=
      +`${process.env.REACT_APP_ACTIVE_TIMER}`
    );
  };

  const [showIEButton, setShowIEButton] = useState(checkActivationTime);

  useEffect(() => {
    // Initial check
    setShowIEButton(checkActivationTime());

    // Set up interval to check every 5 minutes
    const interval = setInterval(() => {
      setShowIEButton(checkActivationTime());
    }, 5 * 60 * 1000); // 5 minutes in milliseconds

    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, [activatedAt]); // Depend on activatedAt to re-run if it changes

  return (
    <div
      className={`${isPlaceholderData ? styles.placeholder : ""} ${
        styles.propertyList
      } ${className}`}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      onClick={() => {
        scrollToTop();
        if (loginType === "investor") {
          NavigatePropertyDetail({
            propertyId: id,
            propertySFID: objPropertyDetails?.propertySFID,
            opportunitySFID: objPropertyDetails?.opportunitySFID || "",
            pubPropertySFID: objPropertyDetails?.pubPropertySFID || "",
            shareId: shareKey,
            address: objPropertyDetails?.address,
            bedRooms: objPropertyDetails?.bedRooms,
            baths: objPropertyDetails?.baths,
            sqft: objPropertyDetails?.sqft,
            askingPrice: objPropertyDetails?.askingPrice
          });
          navigate(`/external/${shareKey}`);
        } else {
          navigate(`/internal/${propertyId}`);
        }
      }}
    >
      <div className={styles.imgBox}>
        <div
          className={`${styles.cardCaption} ${imageUrl ? styles.WithImg : ""}`}
        >
          <div className={styles.profileWrap}>
            <Tag
              value={statusValue}
              style={{
                background: `${status?.bgColor || "#0000008a"}`,
                color: `${status?.txtColor || "#ffffff"}`
              }}
            />
            {loginType === "agent" && (
              <Tag
                value={
                  countDaysFromDate(objPropertyDetails?.createdAt) > 1
                    ? `${countDaysFromDate(
                        objPropertyDetails?.createdAt
                      )} Days ago`
                    : `${countDaysFromDate(
                        objPropertyDetails?.createdAt
                      )} Day ago`
                }
                className={styles.tagClass}
                style={{ background: "#0000008a", color: "#ffffff" }}
              />
            )}
          </div>
          <div className={styles.profileImg}>
            <div className={styles.agentWrap}>
              {loginType === "agent" && (
                <>
                  <AvatarC
                    label={`${agentFirstName?.slice(
                      0,
                      1
                    )}${agentLastName?.slice(0, 1)}`}
                    shape={shape}
                  />
                  <div>
                    <div
                      className={`${styles.text} ${
                        !imageUrl ? styles.WithoutImg : ""
                      }`}
                    >
                      {listedTitle}
                    </div>
                    <div
                      className={`${styles.name} ${
                        !imageUrl ? styles.WithoutImg : ""
                      }`}
                    >
                      {agentFirstName} {agentLastName}
                    </div>
                  </div>
                </>
              )}
            </div>
            <Tag
              value={salesInstruction}
              style={{ background: "#0000008a", color: "#ffffff" }}
            />
          </div>
        </div>
        <img
          className={styles.Image}
          src={imageUrl ?? "/images/default-property.jpg"}
          alt=""
          width="50%"
          // onError={(e: any) => {
          //   e.target.src = "/images/default-property.jpg";
          //   e.target.alt = "Image not available";
          // }}
        />
      </div>
      <div className={styles.propertyInfo}>
        <div className={styles.priceWrap}>
          <div className={styles.priceWrap}>
            <div className={styles.amount}>
              {formatCurrency(amount1, currencyCode, locale, 0, 0)}
            </div>
            <PriceDropTag
              type={loginType}
              priceDrop={objPropertyDetails?.priceDrop}
              priceDropDate={objPropertyDetails?.priceDropDate}
            />
          </div>
          <div onClick={(e) => e.preventDefault()}>
            <Like
              propertyId={propertyId}
              favCount={+favCount}
              width={25}
              isMobile={true}
              loginType={loginType}
              objPropertyDetails={objPropertyDetails}
            />
          </div>
        </div>
        <div className={styles.accessoriesListing}>
          {bedRooms && (
            <span className={styles.accessories}>{bedRooms} Bed</span>
          )}
          {totalBath && (
            <span className={styles.accessories}>{totalBath} Bath</span>
          )}
          {sqft && (
            <span className={styles.accessories}>
              {numberWithCommas(sqft)} Sq.Ft
            </span>
          )}
        </div>
        <div className={styles.addressWrap}>
          <div>
            <div className={styles.address}>{street}</div>
            <div className={styles.location}>
              {city}, {state} {postalCode}
            </div>
          </div>
          {loginType === "agent" && (
            <>
              {type !== "popup" && statusValue === "Active" && (
                <div
                  className={`exchange-${id} ${styles.findInvestor} ${
                    checkActivationTime() ? styles.openButton : ""
                  }`}
                  onClick={(e) => {
                    e?.stopPropagation();
                    navigate(
                      `/flipper-vault?pid=${id}&sfid=${sfid}&lat=${lat}&lng=${lng}&radius=60&shouldIgnoreRadius=true&price=${amount}&street=${encodeURIComponent(street || "")}&city=${encodeURIComponent(city || "")}&state=${encodeURIComponent(state || "")}&postalCode=${encodeURIComponent(postalCode || "")}&order=High+to+Low&showIEButton=${showIEButton}&view=ALL+ACCOUNTS`
                    );
                    NavigateToExchange({
                      property_id: id || "",
                      property_sfid: sfid || ""
                    });
                  }}
                >
                  <Tooltip
                    position="bottom"
                    target={`.exchange-${id}`}
                    className={styles.tooltip}
                    content={moment(activatedAt)
                      .utc()
                      .add(48, "hours")
                      .local()
                      .format("MM/DD/YY hh:mm A")}
                  />
                  <img
                    src="/images/exchange.svg"
                    alt=""
                    width={20}
                    height={20}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(PropertyListCard);
